import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Rnd } from "react-rnd";
import { Button, Radio } from "antd";
import { AutoModel, VideoShow } from "../../components/common";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import { clampRoiToContainRect, getContainFitTransform } from "./autoSubtitleGeometry";
import "./UploadVideo.scss";

/** 与 PremiumUploadVideoWorkspacePage 条带项对齐的最小字段 */
export type PremiumAutoSubtitleVideoRow = {
  uid: string;
  name: string;
  previewUrl?: string;
  thumbnailUrl?: string;
  file?: File;
  resourceId?: number;
  episode?: {
    coverUrl: string;
    playRow: {
      result_url?: string | null;
      pure_video_url?: string | null;
      file_url?: string | null;
      name: string;
    };
  };
  subtitleExtractType?: string | null;
  ocrBbox?: string[] | null;
};

export type PremiumAutoSubtitleModalHandle = {
  buildAutoSubtitleParams: () => Task.AutoSubtitleParams | null;
  /** 与 Index handleConfirmGenerate：画面+自定义但 ocrBbox 未就绪 */
  isVideoCustomOcrIncomplete: () => boolean;
};

/** 与 Index `subtitle-auto-panel` 展示一致：供右侧摘要同步 */
export type PremiumAutoSubtitleParamsPreview = {
  extract: "vision" | "audio";
  region: "middle" | "custom";
};

type Props = {
  open: boolean;
  onClose: () => void;
  afterOpenChange?: (open: boolean) => void;
  videoFiles: PremiumAutoSubtitleVideoRow[];
  setVideoFiles: React.Dispatch<React.SetStateAction<PremiumAutoSubtitleVideoRow[]>>;
  selectedVideoIndex: number;
  showWarning: (msg: string) => void;
  /** 弹窗内切换画面/音频或识别区域时同步到右侧虚线摘要区 */
  onParamsPreviewChange?: (preview: PremiumAutoSubtitleParamsPreview) => void;
};

const ROI_SNAP_THRESHOLD = 10;

export const PremiumAutoSubtitleModal = forwardRef<PremiumAutoSubtitleModalHandle, Props>(
  function PremiumAutoSubtitleModal(
    {
      open,
      onClose,
      afterOpenChange,
      videoFiles,
      setVideoFiles,
      selectedVideoIndex,
      showWarning,
      onParamsPreviewChange,
    },
    ref
  ) {
    const [autoSubtitleModalEntered, setAutoSubtitleModalEntered] = useState(false);
    const [autoSubtitleMode, setAutoSubtitleMode] = useState<"video" | "audio">("video");
    const [autoSubtitleRegion, setAutoSubtitleRegion] = useState<"middle" | "custom">("middle");
    const [customSubtitleRoi, setCustomSubtitleRoi] = useState({ x: 40, y: 320, width: 200, height: 100 });
    const [roiSnappingH, setRoiSnappingH] = useState(false);
    const [roiSnappingV, setRoiSnappingV] = useState(false);
    const autoVideoInnerRef = useRef<HTMLDivElement | null>(null);
    const autoSubtitleInitTokenRef = useRef("");
    const [roiRenderKey, setRoiRenderKey] = useState(0);
    const [autoSubtitleUserSnapshot, setAutoSubtitleUserSnapshot] = useState<{
      videoUid: string;
      videoResourceId?: number;
      mode: "video" | "audio";
      region: "middle" | "custom";
      relativeRoi: { x: number; y: number; width: number; height: number } | null;
      pixelRoi: { x: number; y: number; width: number; height: number } | null;
    } | null>(null);
    const autoSubtitleRoiResizeObserverRef = useRef<ResizeObserver | null>(null);
    const [autoSubtitleRoiReady, setAutoSubtitleRoiReady] = useState(false);

    useEffect(() => {
      if (!open || !onParamsPreviewChange) return;
      onParamsPreviewChange({
        extract: autoSubtitleMode === "video" ? "vision" : "audio",
        region: autoSubtitleRegion,
      });
    }, [open, autoSubtitleMode, autoSubtitleRegion, onParamsPreviewChange]);

    const autoSubtitleVideoListIdentity = videoFiles.map((v) => `${v.uid}:${v.subtitleExtractType ?? ""}`).join("|");

    const getAutoSubtitleContainLayout = useCallback((): {
      cw: number;
      ch: number;
      scale: number;
      offsetX: number;
      offsetY: number;
      displayedW: number;
      displayedH: number;
    } | null => {
      const inner = autoVideoInnerRef.current;
      const videoEl = inner?.querySelector("video") as HTMLVideoElement | null;
      if (!inner || !videoEl || videoEl.videoWidth <= 0 || videoEl.videoHeight <= 0) return null;
      const cw = inner.offsetWidth;
      const ch = inner.offsetHeight;
      if (cw <= 0 || ch <= 0) return null;
      return {
        cw,
        ch,
        ...getContainFitTransform(videoEl.videoWidth, videoEl.videoHeight, cw, ch),
      };
    }, []);

    useEffect(() => {
      if (!open) {
        autoSubtitleInitTokenRef.current = "";
        setAutoSubtitleModalEntered(false);
        return;
      }
      setAutoSubtitleRoiReady(false);
    }, [open]);

    useEffect(() => {
      if (!open || !autoSubtitleModalEntered) return;

      const token = Math.random().toString(36).substring(7);
      autoSubtitleInitTokenRef.current = token;

      const applyDefaultRoi = (): boolean => {
        const hasSelected = selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex];
        const selectedVideo = hasSelected
          ? videoFiles[selectedVideoIndex]
          : videoFiles.length > 0
            ? videoFiles[0]
            : null;
        if (!selectedVideo) return false;

        const cw = autoVideoInnerRef.current?.offsetWidth;
        const ch = autoVideoInnerRef.current?.offsetHeight;

        const snapshotMatched = Boolean(autoSubtitleUserSnapshot);

        if (snapshotMatched) {
          const snapshot = autoSubtitleUserSnapshot;
          if (!snapshot) return false;

          if (snapshot.mode === "audio") {
            setAutoSubtitleMode("audio");
            setAutoSubtitleRegion("middle");
            return true;
          }

          setAutoSubtitleMode("video");
          if (snapshot.region === "middle") {
            setAutoSubtitleRegion("middle");
            return true;
          }

          if (snapshot.region === "custom" && cw && ch) {
            const layoutRestore = getAutoSubtitleContainLayout();
            if (!layoutRestore) return false;
            const { offsetX: ox, offsetY: oy, displayedW: dw, displayedH: dh, scale } = layoutRestore;
            let nextRaw: { x: number; y: number; width: number; height: number };
            if (snapshot.pixelRoi) {
              const p = snapshot.pixelRoi;
              nextRaw = {
                x: ox + p.x * scale,
                y: oy + p.y * scale,
                width: p.width * scale,
                height: p.height * scale,
              };
            } else if (snapshot.relativeRoi) {
              nextRaw = {
                x: ox + snapshot.relativeRoi.x * dw,
                y: oy + snapshot.relativeRoi.y * dh,
                width: snapshot.relativeRoi.width * dw,
                height: snapshot.relativeRoi.height * dh,
              };
            } else {
              return false;
            }
            const nextRoi = clampRoiToContainRect(nextRaw, ox, oy, dw, dh);
            setCustomSubtitleRoi(nextRoi);
            setAutoSubtitleRegion("custom");
            return true;
          }

          return false;
        }

        if (!cw || !ch) return false;
        const layoutDefault = getAutoSubtitleContainLayout();
        if (!layoutDefault) return false;
        const { offsetX: ox, offsetY: oy, displayedW: dw, displayedH: dh } = layoutDefault;
        const overlayW = dw * 0.8;
        const overlayH = dh * 0.2;
        const overlayX = ox + dw * 0.1;
        const overlayY = oy + dh * 0.7;

        setCustomSubtitleRoi(
          clampRoiToContainRect({ x: overlayX, y: overlayY, width: overlayW, height: overlayH }, ox, oy, dw, dh)
        );

        setAutoSubtitleMode("video");
        setAutoSubtitleRegion("middle");
        return true;
      };

      const tryInit = () => {
        if (autoSubtitleInitTokenRef.current !== token) return;
        const container = autoVideoInnerRef.current;
        const w = container?.offsetWidth || 0;
        if (!container || w === 0) {
          setTimeout(tryInit, 100);
          return;
        }
        const success = applyDefaultRoi();
        if (success) {
          setAutoSubtitleRoiReady(true);
        } else {
          setTimeout(tryInit, 100);
        }
      };

      let rafOuter = 0;
      let rafInner = 0;
      rafOuter = requestAnimationFrame(() => {
        rafInner = requestAnimationFrame(tryInit);
      });

      return () => {
        cancelAnimationFrame(rafOuter);
        cancelAnimationFrame(rafInner);
        autoSubtitleInitTokenRef.current = "";
      };
    }, [
      open,
      autoSubtitleModalEntered,
      autoSubtitleVideoListIdentity,
      autoSubtitleUserSnapshot,
      getAutoSubtitleContainLayout,
      videoFiles,
    ]);

    useEffect(() => {
      if (!autoSubtitleUserSnapshot) return;
      if (autoSubtitleUserSnapshot.videoResourceId !== undefined) return;
      const matched = videoFiles.find((v) => v.uid === autoSubtitleUserSnapshot.videoUid && v.resourceId !== undefined);
      if (!matched?.resourceId) return;
      setAutoSubtitleUserSnapshot((prev) => {
        if (!prev) return prev;
        if (prev.videoResourceId !== undefined) return prev;
        if (prev.videoUid !== matched.uid) return prev;
        return { ...prev, videoResourceId: matched.resourceId };
      });
    }, [autoSubtitleUserSnapshot, videoFiles]);

    useEffect(() => {
      if (!open || !autoSubtitleRoiReady) return;
      if (autoSubtitleMode !== "video" || autoSubtitleRegion !== "custom") return;
      const el = autoVideoInnerRef.current;
      if (!el || typeof ResizeObserver === "undefined") return;

      let lastSig: string | null = null;
      let t: ReturnType<typeof setTimeout> | undefined;
      const bumpIfLayoutChanged = () => {
        const inner = autoVideoInnerRef.current;
        const videoEl = inner?.querySelector("video") as HTMLVideoElement | null;
        if (!inner || !videoEl || videoEl.videoWidth <= 0 || videoEl.videoHeight <= 0) return;
        const cw = inner.offsetWidth;
        const ch = inner.offsetHeight;
        if (cw <= 0 || ch <= 0) return;
        const { offsetX, offsetY, displayedW, displayedH } = getContainFitTransform(
          videoEl.videoWidth,
          videoEl.videoHeight,
          cw,
          ch
        );
        const sig = [cw, ch, offsetX, offsetY, displayedW, displayedH].map((n) => n.toFixed(2)).join("|");
        if (lastSig === null) {
          lastSig = sig;
          return;
        }
        if (sig === lastSig) return;
        lastSig = sig;
        setRoiRenderKey((k) => k + 1);
      };

      const schedule = () => {
        if (t) clearTimeout(t);
        t = setTimeout(bumpIfLayoutChanged, 60);
      };

      const ro = new ResizeObserver(schedule);
      ro.observe(el);
      bumpIfLayoutChanged();
      return () => {
        ro.disconnect();
        if (t) clearTimeout(t);
      };
    }, [open, autoSubtitleRoiReady, autoSubtitleMode, autoSubtitleRegion]);

    const getCustomSubtitleRelativeRoi = useCallback((): { x: number; y: number; width: number; height: number } | null => {
      if (autoSubtitleRegion !== "custom" || !autoVideoInnerRef.current) return null;
      const layout = getAutoSubtitleContainLayout();
      if (!layout) return null;
      const { offsetX, offsetY, displayedW, displayedH } = layout;
      if (displayedW <= 0 || displayedH <= 0) return null;
      return {
        x: (customSubtitleRoi.x - offsetX) / displayedW,
        y: (customSubtitleRoi.y - offsetY) / displayedH,
        width: customSubtitleRoi.width / displayedW,
        height: customSubtitleRoi.height / displayedH,
      };
    }, [autoSubtitleRegion, customSubtitleRoi, getAutoSubtitleContainLayout]);

    const logBananaRoiAdjust = useCallback(
      (_overlay: { x: number; y: number; width: number; height: number }) => {
        /* 与 Index 一致可在此打日志；精品页省略 console */
      },
      [autoSubtitleMode, autoSubtitleRegion]
    );

    const handleRoiDrag = useCallback(
      (_e: unknown, d: { x: number; y: number }) => {
        const layout = getAutoSubtitleContainLayout();
        if (!layout) return;
        const { offsetX, offsetY, displayedW, displayedH } = layout;
        const cxVideo = offsetX + displayedW / 2;
        const cyVideo = offsetY + displayedH / 2;
        const centerX = d.x + customSubtitleRoi.width / 2;
        const centerY = d.y + customSubtitleRoi.height / 2;
        const snapH = Math.abs(centerX - cxVideo) < ROI_SNAP_THRESHOLD;
        const snapV = Math.abs(centerY - cyVideo) < ROI_SNAP_THRESHOLD;
        setRoiSnappingH((prev) => (prev !== snapH ? snapH : prev));
        setRoiSnappingV((prev) => (prev !== snapV ? snapV : prev));
      },
      [customSubtitleRoi.width, customSubtitleRoi.height, getAutoSubtitleContainLayout]
    );

    const handleRoiDragStop = useCallback(
      (_e: unknown, d: { x: number; y: number }) => {
        const layout = getAutoSubtitleContainLayout();
        let finalX = d.x;
        let finalY = d.y;
        if (layout) {
          const { offsetX, offsetY, displayedW, displayedH } = layout;
          const cxVideo = offsetX + displayedW / 2;
          const cyVideo = offsetY + displayedH / 2;
          const centerX = d.x + customSubtitleRoi.width / 2;
          const centerY = d.y + customSubtitleRoi.height / 2;
          if (Math.abs(centerX - cxVideo) < ROI_SNAP_THRESHOLD) {
            finalX = cxVideo - customSubtitleRoi.width / 2;
          }
          if (Math.abs(centerY - cyVideo) < ROI_SNAP_THRESHOLD) {
            finalY = cyVideo - customSubtitleRoi.height / 2;
          }
          const clamped = clampRoiToContainRect(
            { x: finalX, y: finalY, width: customSubtitleRoi.width, height: customSubtitleRoi.height },
            offsetX,
            offsetY,
            displayedW,
            displayedH
          );
          finalX = clamped.x;
          finalY = clamped.y;
        }
        setCustomSubtitleRoi((prev) => ({ ...prev, x: finalX, y: finalY }));
        logBananaRoiAdjust({
          x: finalX,
          y: finalY,
          width: customSubtitleRoi.width,
          height: customSubtitleRoi.height,
        });
        setRoiSnappingH(false);
        setRoiSnappingV(false);
      },
      [customSubtitleRoi.width, customSubtitleRoi.height, getAutoSubtitleContainLayout, logBananaRoiAdjust]
    );

    const handleRoiDragWithClamp = useCallback(
      (e: unknown, d: { x: number; y: number }) => {
        const layout = getAutoSubtitleContainLayout();
        if (!layout) {
          handleRoiDrag(e, d);
          return;
        }
        const { offsetX, offsetY, displayedW, displayedH } = layout;
        const c = clampRoiToContainRect(
          { x: d.x, y: d.y, width: customSubtitleRoi.width, height: customSubtitleRoi.height },
          offsetX,
          offsetY,
          displayedW,
          displayedH
        );
        setCustomSubtitleRoi((prev) => ({ ...prev, x: c.x, y: c.y }));
        handleRoiDrag(e, { x: c.x, y: c.y });
      },
      [handleRoiDrag, getAutoSubtitleContainLayout, customSubtitleRoi.width, customSubtitleRoi.height]
    );

    const handleRoiResizeStop = useCallback(
      (_e: unknown, _direction: unknown, refEl: HTMLElement, _delta: unknown, position: { x: number; y: number }) => {
        const layout = getAutoSubtitleContainLayout();
        let w = parseInt(refEl.style.width, 10);
        let h = parseInt(refEl.style.height, 10);
        let x = position.x;
        let y = position.y;
        if (Number.isNaN(w) || w <= 0) w = customSubtitleRoi.width;
        if (Number.isNaN(h) || h <= 0) h = customSubtitleRoi.height;
        if (layout) {
          const c = clampRoiToContainRect(
            { x, y, width: w, height: h },
            layout.offsetX,
            layout.offsetY,
            layout.displayedW,
            layout.displayedH
          );
          w = c.width;
          h = c.height;
          x = c.x;
          y = c.y;
        }
        setCustomSubtitleRoi({ width: w, height: h, x, y });
        logBananaRoiAdjust({ x, y, width: w, height: h });
      },
      [getAutoSubtitleContainLayout, customSubtitleRoi.width, customSubtitleRoi.height, logBananaRoiAdjust]
    );

    const buildAutoSubtitleParams = useCallback((): Task.AutoSubtitleParams | null => {
      const videoTaskIds = videoFiles.filter((v) => v.resourceId !== undefined).map((v) => v.resourceId as number);
      if (videoTaskIds.length === 0) return null;

      const params: Task.AutoSubtitleParams = {
        taskIds: videoTaskIds,
        subtitleExtractType: autoSubtitleMode === "video" ? "vision" : "audio",
      };

      if (!(autoSubtitleMode === "video" && autoSubtitleRegion === "custom")) {
        delete (params as { ocrBbox?: string[] }).ocrBbox;
        return params;
      }

      const pixelRoi = autoSubtitleUserSnapshot?.pixelRoi;
      if (pixelRoi) {
        params.ocrBbox = [
          Math.round(pixelRoi.x).toString(),
          Math.round(pixelRoi.y).toString(),
          Math.round(pixelRoi.width).toString(),
          Math.round(pixelRoi.height).toString(),
        ];
      } else {
        const videoEl = autoVideoInnerRef.current?.querySelector("video") as HTMLVideoElement | null;
        const cw = autoVideoInnerRef.current?.offsetWidth || 0;
        const ch = autoVideoInnerRef.current?.offsetHeight || 0;
        if (videoEl && videoEl.videoWidth > 0 && videoEl.videoHeight > 0 && cw > 0 && ch > 0) {
          const { scale, offsetX, offsetY } = getContainFitTransform(videoEl.videoWidth, videoEl.videoHeight, cw, ch);
          const x = (customSubtitleRoi.x - offsetX) / scale;
          const y = (customSubtitleRoi.y - offsetY) / scale;
          const width = customSubtitleRoi.width / scale;
          const height = customSubtitleRoi.height / scale;
          params.ocrBbox = [
            Math.round(x).toString(),
            Math.round(y).toString(),
            Math.round(width).toString(),
            Math.round(height).toString(),
          ];
        }
      }

      return params;
    }, [
      videoFiles,
      autoSubtitleMode,
      autoSubtitleRegion,
      autoSubtitleUserSnapshot,
      customSubtitleRoi.x,
      customSubtitleRoi.y,
      customSubtitleRoi.width,
      customSubtitleRoi.height,
    ]);

    const isVideoCustomOcrIncomplete = useCallback(() => {
      if (autoSubtitleMode !== "video" || autoSubtitleRegion !== "custom") return false;
      const p = buildAutoSubtitleParams();
      return !p?.ocrBbox || p.ocrBbox.length !== 4;
    }, [autoSubtitleMode, autoSubtitleRegion, buildAutoSubtitleParams]);

    useImperativeHandle(
      ref,
      () => ({
        buildAutoSubtitleParams,
        isVideoCustomOcrIncomplete,
      }),
      [buildAutoSubtitleParams, isVideoCustomOcrIncomplete]
    );

    const handleAutoSubtitleModalConfirm = () => {
      const hasSelected = selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex];
      const video = hasSelected
        ? videoFiles[selectedVideoIndex]
        : videoFiles.length > 0
          ? videoFiles[0]
          : null;

      if (video) {
        if (autoSubtitleMode === "video" && autoSubtitleRegion === "custom" && !autoSubtitleRoiReady) {
          showWarning("正在加载识别区域坐标，请稍候后再点击确定");
          return;
        }

        const relativeRoi =
          autoSubtitleMode === "video" && autoSubtitleRegion === "custom" ? getCustomSubtitleRelativeRoi() : null;

        const videoEl = autoVideoInnerRef.current?.querySelector("video") as HTMLVideoElement | null;
        const pixelRoi = (() => {
          if (
            autoSubtitleMode !== "video" ||
            autoSubtitleRegion !== "custom" ||
            !videoEl ||
            videoEl.videoWidth <= 0 ||
            videoEl.videoHeight <= 0
          ) {
            return null;
          }
          const cw = autoVideoInnerRef.current?.offsetWidth || 0;
          const ch = autoVideoInnerRef.current?.offsetHeight || 0;
          if (cw <= 0 || ch <= 0) return null;
          const { scale, offsetX, offsetY } = getContainFitTransform(videoEl.videoWidth, videoEl.videoHeight, cw, ch);
          const x = (customSubtitleRoi.x - offsetX) / scale;
          const y = (customSubtitleRoi.y - offsetY) / scale;
          const width = customSubtitleRoi.width / scale;
          const height = customSubtitleRoi.height / scale;
          return {
            x: Math.round(x),
            y: Math.round(y),
            width: Math.round(width),
            height: Math.round(height),
          };
        })();

        setAutoSubtitleUserSnapshot({
          videoUid: video.uid,
          videoResourceId: video.resourceId,
          mode: autoSubtitleMode,
          region: autoSubtitleRegion,
          relativeRoi,
          pixelRoi,
        });

        setVideoFiles((prev) => {
          const subtitleExtractType = autoSubtitleMode === "video" ? "vision" : "audio";
          const ocrBbox =
            autoSubtitleMode === "video" && autoSubtitleRegion === "custom" && pixelRoi
              ? [
                  Math.round(pixelRoi.x).toString(),
                  Math.round(pixelRoi.y).toString(),
                  Math.round(pixelRoi.width).toString(),
                  Math.round(pixelRoi.height).toString(),
                ]
              : null;

          return prev.map((vf) => ({ ...vf, subtitleExtractType, ocrBbox }));
        });
      }

      onClose();
    };

    const handleAutoSubtitleModalCancel = () => {
      if (autoSubtitleMode === "video" && autoSubtitleRegion === "custom" && !autoSubtitleRoiReady) {
        onClose();
        return;
      }
      handleAutoSubtitleModalConfirm();
    };

    const stripVideoSrc = (video: PremiumAutoSubtitleVideoRow): string | undefined => {
      if (video.previewUrl) return video.previewUrl;
      if (video.episode) return getVideoPlayUrl(video.episode.playRow) || undefined;
      return undefined;
    };

    const stripPoster = (video: PremiumAutoSubtitleVideoRow) =>
      video.thumbnailUrl || video.episode?.coverUrl;

    return (
      <AutoModel
        title="自动识别字幕"
        open={open}
        afterOpenChange={(o) => {
          setAutoSubtitleModalEntered(o);
          afterOpenChange?.(o);
        }}
        onClose={handleAutoSubtitleModalCancel}
        contentMaxWidth={920}
        contentClassName="auto-subtitle-modal"
        overlayClassName="upload-video-modal-overlay"
        footerRight={<>
          <Button onClick={handleAutoSubtitleModalCancel}>取消</Button>
          <Button
            type="primary"
            disabled={autoSubtitleMode === "video" && autoSubtitleRegion === "custom" && !autoSubtitleRoiReady}
            onClick={handleAutoSubtitleModalConfirm}
          >
            确定
          </Button>
        </>}
      >
        <div className="auto-subtitle-modal-body">
          <div className="auto-subtitle-left">
            <div className="auto-subtitle-left-inner">
              {(() => {
                const hasSelected = selectedVideoIndex >= 0 && videoFiles[selectedVideoIndex];
                const video = hasSelected
                  ? videoFiles[selectedVideoIndex]
                  : videoFiles.length > 0
                    ? videoFiles[0]
                    : null;
                const src = video ? stripVideoSrc(video) : undefined;
                return (
                  <VideoShow
                    videoSrc={src}
                    poster={video ? stripPoster(video) : undefined}
                    innerRef={autoVideoInnerRef}
                  >
                    {autoSubtitleMode === "video" && autoSubtitleRegion === "custom" && autoSubtitleRoiReady && (
                      <>
                        {roiSnappingH &&
                          (() => {
                            const L = getAutoSubtitleContainLayout();
                            const lineStyle = L
                              ? {
                                  left: L.offsetX + L.displayedW / 2,
                                  transform: "translateX(-50%)" as const,
                                  right: "auto" as const,
                                }
                              : undefined;
                            return (
                              <div
                                className="auto-subtitle-snap-line auto-subtitle-snap-line-v"
                                style={lineStyle}
                                aria-hidden
                              />
                            );
                          })()}
                        {roiSnappingV &&
                          (() => {
                            const L = getAutoSubtitleContainLayout();
                            const lineStyle = L
                              ? {
                                  top: L.offsetY + L.displayedH / 2,
                                  transform: "translateY(-50%)" as const,
                                  bottom: "auto" as const,
                                }
                              : undefined;
                            return (
                              <div
                                className="auto-subtitle-snap-line auto-subtitle-snap-line-h"
                                style={lineStyle}
                                aria-hidden
                              />
                            );
                          })()}
                        <Rnd
                          key={`auto-roi-${roiRenderKey}-${autoSubtitleRegion}-${selectedVideoIndex}`}
                          size={{ width: customSubtitleRoi.width, height: customSubtitleRoi.height }}
                          position={{ x: customSubtitleRoi.x, y: customSubtitleRoi.y }}
                          bounds="parent"
                          minWidth={60}
                          minHeight={30}
                          style={{
                            border: "2px dashed #ff6b00",
                            background: "rgba(255, 107, 0, 0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                          }}
                          onDrag={handleRoiDragWithClamp}
                          onDragStop={handleRoiDragStop}
                          onResizeStop={handleRoiResizeStop}
                        />
                      </>
                    )}
                  </VideoShow>
                );
              })()}
            </div>
          </div>
          <div className="auto-subtitle-right">
            <div className="auto-subtitle-title">字幕提取方式</div>
            <div className="auto-subtitle-tabs">
              <Button
                className={`tab-btn ${autoSubtitleMode === "video" ? "active" : ""}`}
                onClick={() => setAutoSubtitleMode("video")}
              >
                画面识别
              </Button>
              <Button
                className={`tab-btn ${autoSubtitleMode === "audio" ? "active" : ""}`}
                onClick={() => setAutoSubtitleMode("audio")}
              >
                音频识别
              </Button>
            </div>

            {autoSubtitleMode === "video" && (
              <div className="auto-subtitle-region-wrap">
                <div className="auto-subtitle-title">识别区域</div>
                <Radio.Group
                  value={autoSubtitleRegion}
                  onChange={(e) => setAutoSubtitleRegion(e.target.value)}
                >
                  <div className="auto-subtitle-radio-group">
                    <Radio value="middle">预设区域：画面中部靠下</Radio>
                    <Radio value="custom">自定义</Radio>
                  </div>
                </Radio.Group>

                {autoSubtitleRegion === "middle" && (
                  <div className="auto-subtitle-preview">
                    <div className="auto-subtitle-preview-image">
                      <img src="/img/image.png" alt="字幕区域预览" className="auto-subtitle-preview-img" />
                    </div>
                  </div>
                )}
                {autoSubtitleRegion === "custom" &&
                  (() => {
                    const relativeRoi = getCustomSubtitleRelativeRoi();
                    const videoEl = autoVideoInnerRef.current?.querySelector("video") as HTMLVideoElement | null;
                    const cw = autoVideoInnerRef.current?.offsetWidth || 0;
                    const ch = autoVideoInnerRef.current?.offsetHeight || 0;
                    const videoPixelRoi =
                      videoEl &&
                      videoEl.videoWidth > 0 &&
                      videoEl.videoHeight > 0 &&
                      cw > 0 &&
                      ch > 0
                        ? (() => {
                            const { scale, offsetX, offsetY } = getContainFitTransform(
                              videoEl.videoWidth,
                              videoEl.videoHeight,
                              cw,
                              ch
                            );
                            const x = (customSubtitleRoi.x - offsetX) / scale;
                            const y = (customSubtitleRoi.y - offsetY) / scale;
                            const width = customSubtitleRoi.width / scale;
                            const height = customSubtitleRoi.height / scale;
                            return {
                              x: Math.round(x),
                              y: Math.round(y),
                              width: Math.round(width),
                              height: Math.round(height),
                            };
                          })()
                        : null;
                    return (
                      <div className="auto-subtitle-roi-debug">
                        <div className="auto-subtitle-title">区域坐标（提交时可换算为比例）</div>
                        <div className="auto-subtitle-roi-values">
                          <span>
                            像素：X {videoPixelRoi ? videoPixelRoi.x : Math.round(customSubtitleRoi.x)} Y{" "}
                            {videoPixelRoi ? videoPixelRoi.y : Math.round(customSubtitleRoi.y)} | 宽{" "}
                            {videoPixelRoi ? videoPixelRoi.width : Math.round(customSubtitleRoi.width)} 高{" "}
                            {videoPixelRoi ? videoPixelRoi.height : Math.round(customSubtitleRoi.height)}
                          </span>
                          {relativeRoi && (
                            <span className="auto-subtitle-roi-relative">
                              比例：x {relativeRoi.x.toFixed(3)} y {relativeRoi.y.toFixed(3)} | w{" "}
                              {relativeRoi.width.toFixed(3)} h {relativeRoi.height.toFixed(3)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            )}
          </div>
        </div>
      </AutoModel>
    );
  }
);
